import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const PERIODS = [["today", "Today"], ["week", "This week"], ["month", "This month"], ["custom", "Custom"]];
const PRODUCTS = [["all", "All services"], ["ppf", "PPF"], ["velotint", "Velotint"], ["velocera", "Velocera"]];
const COLS = [
  ["enquiries", "WhatsApp enquiries"], ["leads", "Leads generated"], ["booked", "Jobs booked"],
  ["received", "Jobs received"],
];
const SOLD = [["ppf_sold", "PPF sold", "ppf"], ["velotint_sold", "Velotint sold", "velotint"], ["velocera_sold", "Velocera sold", "velocera"]];
const soldCols = (product) => SOLD.filter(([, , p]) => product === "all" || product === p);
const inr = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;
const nice = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "");

export default function SalesStats() {
  const { user } = useAuth();
  const isRep = user.role === "central_sales_team";
  const allowed = isRep || ["network_admin", "ceo"].includes(user.role);
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [product, setProduct] = useState("all");

  const load = (p = period, prod = product) => {
    if (p === "custom" && (!range.start || !range.end)) return;
    api.get("/sales-stats", { params: { period: p, product: prod, ...(p === "custom" ? range : {}) } })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)));
  };
  useEffect(() => { if (allowed) load(period, product); }, [period, product]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!allowed) return <Navigate to="/dashboard" replace />;

  return (
    <div data-testid="sales-stats-page">
      <PageHead title={isRep ? "My Sales Stats" : "Sales Stats · Central Sales B2C"}
        subtitle="A sale is closed when the job reaches Received. Conversion = jobs received ÷ leads. Services sold counts every PPF / Velotint / Velocera service on received jobs." />
      <Tabs value={period} onValueChange={(v) => { setData(null); setPeriod(v); }}>
        <TabsList data-testid="sales-stats-tabs">
          {PERIODS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`sales-stats-tab-${k}`}>{l}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="sales-stats-product-toggle">
        {PRODUCTS.map(([k, l]) => (
          <Button key={k} size="sm" variant={product === k ? "default" : "outline"} className="rounded-full"
            onClick={() => setProduct(k)} data-testid={`sales-stats-product-${k}`}>{l}</Button>
        ))}
      </div>
      {product !== "all" && (
        <p className="mt-2 text-xs text-slate-500" data-testid="sales-stats-product-note">
          Leads, bookings, received jobs and revenue include only jobs with a {PRODUCTS.find((x) => x[0] === product)[1]} service
          (a job with several services shows its full value under each). WhatsApp enquiries are not product-specific.
        </p>
      )}
      {period === "custom" && (
        <div className="mt-4 flex flex-wrap items-end gap-3" data-testid="sales-stats-custom">
          <Input type="date" className="w-44" value={range.start} data-testid="sales-stats-start"
            onChange={(e) => setRange({ ...range, start: e.target.value })} />
          <span className="pb-2 text-sm text-slate-500">to</span>
          <Input type="date" className="w-44" value={range.end} data-testid="sales-stats-end"
            onChange={(e) => setRange({ ...range, end: e.target.value })} />
          <Button onClick={() => load("custom")} disabled={!range.start || !range.end} data-testid="sales-stats-apply">Show</Button>
        </div>
      )}
      {data && (
        <p className="mt-4 text-sm text-slate-500" data-testid="sales-stats-range">
          {nice(data.start)}{data.end !== data.start ? ` – ${nice(data.end)}` : ""}
        </p>
      )}
      {data && (isRep ? <RepCards row={data.rows[0]} product={product} /> : <StatsTable data={data} product={product} />)}
    </div>
  );
}

const RepCards = ({ row, product }) => (
  <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-6" data-testid="sales-stats-mine">
    {[...COLS, ["conversion", "Conversion"], ["revenue", "Revenue"], ...soldCols(product)].map(([k, l]) => (
      <Card key={k} className="border-brand-line shadow-none">
        <CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">{l}</p>
          <p className="mt-1 text-2xl font-semibold text-brand-ink" data-testid={`sales-stats-mine-${k}`}>
            {row ? (k === "conversion" ? `${row[k]}%` : k === "revenue" ? inr(row[k]) : row[k]) : 0}
          </p>
        </CardContent>
      </Card>
    ))}
  </div>
);

const StatsTable = ({ data, product }) => (
  <Card className="mt-3 border-brand-line shadow-none">
    <CardContent className="overflow-x-auto p-0">
      <Table data-testid="sales-stats-table">
        <TableHeader>
          <TableRow>
            <TableHead>Salesperson</TableHead>
            {COLS.map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}
            <TableHead className="text-right">Conversion</TableHead>
            <TableHead className="text-right">Revenue</TableHead>
            {soldCols(product).map(([k, l]) => <TableHead key={k} className="text-right">{l}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow key={r.user_id} data-testid={`sales-stats-row-${r.user_id}`}>
              <TableCell className="font-medium">{r.name}</TableCell>
              {COLS.map(([k]) => <TableCell key={k} className="text-right">{r[k]}</TableCell>)}
              <TableCell className="text-right font-semibold">{r.conversion}%</TableCell>
              <TableCell className="text-right" data-testid={`sales-stats-revenue-${r.user_id}`}>{inr(r.revenue)}</TableCell>
              {soldCols(product).map(([k]) => <TableCell key={k} className="text-right" data-testid={`sales-stats-${k}-${r.user_id}`}>{r[k]}</TableCell>)}
            </TableRow>
          ))}
          {data.total && (
            <TableRow className="bg-slate-50 font-semibold" data-testid="sales-stats-total">
              <TableCell>All salespeople</TableCell>
              {COLS.map(([k]) => <TableCell key={k} className="text-right">{data.total[k]}</TableCell>)}
              <TableCell className="text-right">{data.total.conversion}%</TableCell>
              <TableCell className="text-right" data-testid="sales-stats-total-revenue">{inr(data.total.revenue)}</TableCell>
              {soldCols(product).map(([k]) => <TableCell key={k} className="text-right">{data.total[k]}</TableCell>)}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);
